import { Box, Chip, Stack, TextField, Typography } from "@mui/material";
import { Dialog } from "components/Dialog";
import { RcraInfoCredentialEpaIdMappingsQuery } from "generated-graphql/graphql";
import pluralize from "pluralize";
import { useMemo, useState } from "react";

export const EpaIdDialog = ({
  credential,
  onClose,
}: {
  credential: RcraInfoCredentialEpaIdMappingsQuery["rcraInfoCredentialEpaIdMappings"][number];
  onClose: () => void;
}) => {
  const [epaIdSearch, setEpaIdSearch] = useState<string>("");

  const filteredMappings = useMemo(() => {
    let mappings = credential.epaIds;

    // Filter if search is provided
    if (epaIdSearch.trim()) {
      const searchLower = epaIdSearch.toLowerCase().trim();
      mappings = mappings.filter((mapping) => {
        return (
          mapping.epaId.toLowerCase().includes(searchLower) ||
          mapping.facilities.some(
            (facility) =>
              facility.name?.toLowerCase().includes(searchLower) ||
              facility.state?.toLowerCase().includes(searchLower)
          )
        );
      });
    }

    // Sort alphabetically by EPA ID
    return [...mappings].sort((a, b) => a.epaId.localeCompare(b.epaId));
  }, [credential.epaIds, epaIdSearch]);

  return (
    <Dialog
      open
      onClose={onClose}
      maxWidth="md"
      fullWidth
      title={`${credential.credential.name ?? "Credential"} - EPA IDs`}
      noPrimaryAction
    >
      <Stack spacing={2}>
        <TextField
          label="Search EPA IDs"
          variant="outlined"
          size="small"
          fullWidth
          value={epaIdSearch}
          onChange={(e) => setEpaIdSearch(e.target.value)}
          placeholder="Enter EPA ID to search..."
          autoFocus
        />
        <Box
          sx={{
            maxHeight: 400,
            overflow: "auto",
            border: "1px solid",
            borderColor: "divider",
            borderRadius: 1,
            p: 1,
          }}
        >
          <Stack spacing={1}>
            {filteredMappings.map((mapping) => (
              <Stack key={mapping.epaId} spacing={0.5}>
                <Typography variant="body2">{mapping.epaId}</Typography>
                {mapping.facilities.length > 0 ? (
                  <Stack spacing={0.5} sx={{ pl: 2 }}>
                    {mapping.facilities.map((facility) => (
                      <Stack
                        key={facility.id}
                        direction="row"
                        spacing={1}
                        alignItems="center"
                      >
                        <Chip
                          label={`${facility.name}${
                            facility.state ? ` (${facility.state})` : ""
                          }${` ${facility.tenant?.name}`}`}
                          size="small"
                          variant="outlined"
                        />
                      </Stack>
                    ))}
                  </Stack>
                ) : (
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ pl: 2 }}
                  >
                    No facilities found
                  </Typography>
                )}
              </Stack>
            ))}
            {epaIdSearch.trim() && filteredMappings.length === 0 && (
              <Typography variant="body2" color="text.secondary">
                No EPA IDs found matching "{epaIdSearch}"
              </Typography>
            )}
          </Stack>
        </Box>
        <Typography variant="caption" color="text.secondary" pb={2}>
          Total: {credential.epaIds.length} EPA{" "}
          {pluralize("ID", credential.epaIds.length)}
          {epaIdSearch.trim() &&
            filteredMappings.length !== credential.epaIds.length && (
              <> (showing {filteredMappings.length} matching)</>
            )}
        </Typography>
      </Stack>
    </Dialog>
  );
};
